import cn from "@core/utils/class-names";
import { Badge, Flex, Text } from "rizzui";
import { replaceUnderscoreDash } from "@core/utils/replace-underscore-dash";

const statusColors = {
  success: ["text-green-dark", "bg-green-dark"],
  warning: ["text-orange-dark", "bg-orange-dark"],
  danger: ["text-red-dark", "bg-red-dark"],
  default: ["text-gray-600", "bg-gray-600"],
};

const allStatus = {
  online: statusColors.success,
  offline: statusColors.default,
  pending: statusColors.warning,
  paid: statusColors.success,
  overdue: statusColors.danger,
  completed: statusColors.success,
  cancelled: statusColors.danger,
  publish: statusColors.success,
  approved: statusColors.success,
  rejected: statusColors.danger,
  active: statusColors.success,
  deactivated: statusColors.danger,
  on_going: statusColors.warning,
  at_risk: statusColors.danger,
  delayed: statusColors.default,
  draft: statusColors.default,
  refunded: statusColors.default,
  // Steelyard's own vocabulary (order/invoice/refund states), added directly to this map rather
  // than a parallel badge component — a directed exception to "copied files change only
  // mechanically", so one status→tone rule lives in one place instead of two implementations.
  placed: statusColors.default,
  picking_packing: statusColors.warning,
  // Legacy states merged into picking_packing; old history rows still carry them.
  picking: statusColors.warning,
  packing: statusColors.warning,
  packed: statusColors.warning,
  invoiced: statusColors.success,
  dispatched: statusColors.success,
  delivered: statusColors.success,
  awaiting_payment: statusColors.warning,
  invited: statusColors.warning,
  awaiting_company_approval: statusColors.warning,
  awaiting_approval: statusColors.warning,
  raised: statusColors.warning,
  posted: statusColors.success,
  queued: statusColors.warning,
  failed: statusColors.danger,
  succeeded: statusColors.success,
  // Billing's InvoiceState e-invoicing arms and the e-way bill's recorded status (§17 invoice list).
  registered: statusColors.success,
  registration_failed: statusColors.danger,
  generated: statusColors.success,
  // 008-reporting-dashboards T015: the packing variance report's Band vocabulary (green/amber/red,
  // Warehouse's Band.cs) — colour + text for SC-009's colour-independence requirement, added here
  // rather than a parallel badge component for the same reason the block above was.
  green: statusColors.success,
  amber: statusColors.warning,
  red: statusColors.danger,
  // 005-packing-verification, admin-packing-oversight bucket (T053, T056): PackingTaskStatus
  // (Warehouse.Domain.Packing.PackingTaskStatus.cs) and SpotCheckStatus (SpotCheck.cs) — "cancelled"
  // and "completed" above already cover two of each enum's members, added here for the same reason
  // this block's own comment states: one status→tone rule, not a parallel badge.
  unassigned: statusColors.default,
  assigned: statusColors.warning,
  in_progress: statusColors.warning,
  complete: statusColors.success,
  conflicted: statusColors.danger,
  selected: statusColors.warning,
  skipped: statusColors.default,
  // Identity's back-office inbox (NoticeSeverity): the notifications page's severity column.
  info: statusColors.default,
  warning: statusColors.warning,
  critical: statusColors.danger,
  unread: statusColors.warning,
  read: statusColors.default,
  // Customers' newsletter subscribers list.
  subscribed: statusColors.success,
  unsubscribed: statusColors.default,
  // Content → Pages: whether a page's text has been published yet (the API answers 404 until it is).
  published: statusColors.success,
  not_published: statusColors.warning,
};

export type StatusTypes = keyof typeof allStatus;

// Where "underscores to spaces" reads wrong; `capitalize` (CSS) still title-cases the result.
const LABELS: Partial<Record<string, string>> = { picking_packing: "picking / packing" };
const label = (status: string) => LABELS[status] ?? replaceUnderscoreDash(status);

export function getStatusBadge(status: string) {
  const statusLower = status.toLowerCase() as StatusTypes;
  if (statusLower in allStatus) {
    return (
      <Flex align="center" gap="2" className="w-auto">
        <Badge renderAsDot className={allStatus[statusLower][1]} />
        <Text
          className={cn("font-medium capitalize", allStatus[statusLower][0])}
        >
          {label(statusLower)}
        </Text>
      </Flex>
    );
  }
  return (
    <Flex align="center" gap="2" className="w-auto">
      <Badge renderAsDot className="bg-gray-600" />
      <Text className="font-medium capitalize text-gray-600">
        {label(statusLower)}
      </Text>
    </Flex>
  );
}
